import { X, Plus, Save } from "lucide-react";
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { PackagesTableContent } from './PackagesTableContent';
import { useSelector, useDispatch } from 'react-redux';
import { setExtractedData } from '../../../store/reducers/index';
import { getDeclarationSummaryTranslation, getMainTranslation } from '../../../hooks/useLanguage';
import { deepClone } from '../../../utils/dataHelpers';

export const PackagesTableModal = ({
  isOpen,
  onClose,
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const [embalajesModalOpen, setEmbalajesModalOpen] = useState(false);
  const [currentEmbalajeIndex, setCurrentEmbalajeIndex] = useState(null);
  const [originalItemData, setOriginalItemData] = useState(null);
  const [isModalDirty, setIsModalDirty] = useState(false);
  const prevIsOpenRef = useRef(false);
  const dispatch = useDispatch();
  const extractedData = useSelector((state) => state.documents.extractedData);

  // Store original item data when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened - capture original data
      const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
      if (currentItem) {
        setOriginalItemData(deepClone(currentItem.embalajes || []));
        setIsModalDirty(false);
      }
    } else if (!isOpen && prevIsOpenRef.current) {
      // Modal just closed - reset
      setOriginalItemData(null);
      setIsModalDirty(false);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, declarationIndex, itemIndex, editableData]);

  // Track changes in the modal
  useEffect(() => {
    if (isOpen && originalItemData !== null) {
      const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
      if (currentItem) {
        const currentEmbalajes = currentItem.embalajes || [];
        const hasChanges = JSON.stringify(currentEmbalajes) !== JSON.stringify(originalItemData);
        setIsModalDirty(hasChanges);
      }
    }
  }, [isOpen, editableData, declarationIndex, itemIndex, originalItemData]);

  const handleSave = () => {
    // CRITICAL: Update extractedData FIRST so that updateEditableData can detect this as a save operation This ensures originalDataRef is updated to the saved state for proper cancel functionality
    dispatch(setExtractedData(editableData));
    // Then update editableData - this will detect the save and update originalDataRef
    setEditableData(editableData);
    setIsDirty(false);
    
    // Update originalItemData to current state so buttons disappear
    const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
    if (currentItem) {
      setOriginalItemData(deepClone(currentItem.embalajes || []));
      setIsModalDirty(false);
    }
    toast.success(getMainTranslation('changesSaved', isSpanish));
  };

  const handleCancel = () => {
    if (originalItemData === null) {
      setIsModalDirty(false);
      return;
    }
    
    const updatedData = [...editableData];
    const partida = updatedData[declarationIndex]?.partidas?.[itemIndex];
    
    if (partida) {
      partida.embalajes = deepClone(originalItemData);
      setEditableData(updatedData);
    }
    
    setIsModalDirty(false);
    toast.success(getMainTranslation('changesCancelled', isSpanish));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-[95vw] h-[90vh] flex flex-col animate-modalEnter">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b">
          <h2 className="text-2xl font-bold">
            {!isSpanish ? "Packages" : "Embalajes"} ({item.embalajes?.length || 0})
          </h2>
          <div className="flex items-center gap-2">
            {/* Save/Cancel Buttons */}
            {isModalDirty && (
              <>
                <button
                  className="bg-red-50 rounded-full flex items-center py-2 px-3 gap-2"
                  onClick={handleCancel}
                  title={!isSpanish ? "Cancel Changes" : "Cancelar Cambios"}
                >
                  <X className="size-4 stroke-red-500" />
                  <span className="text-xs text-red-500">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                </button>
                <button
                  className="bg-primary rounded-full flex items-center py-2 px-3 gap-2"
                  onClick={handleSave}
                  title={!isSpanish ? "Save Changes" : "Guardar Cambios"}
                >
                  <Save className="size-4" />
                  <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title={!isSpanish ? "Close" : "Cerrar"}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 items-center mb-4 justify-end">
          <button
            className="py-1 text-xs flex items-center gap-1"
            onClick={() => {
              const updatedData = [...editableData];
              if (!updatedData[declarationIndex].partidas[itemIndex].embalajes)
                updatedData[declarationIndex].partidas[itemIndex].embalajes = [];
              updatedData[declarationIndex].partidas[itemIndex].embalajes.push({
                tipo: "",
                cantidad: "",
                cantidad_restante: "",
                piezas: "",
                es_interno_o_externo: "",
                marcas: [],
              });
              setEditableData(updatedData);
              setIsDirty(true);
            }}
          >
            <Plus className={`size-4`} />
            {isSpanish ? "Agregar Embalaje" : "Add Package"}
          </button>
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-auto">
          <PackagesTableContent
            declarationIndex={declarationIndex}
            itemIndex={itemIndex}
            isSpanish={isSpanish}
            item={item}
            editableData={editableData}
            setEditableData={setEditableData}
            setIsDirty={setIsDirty}
            embalajesModalOpen={embalajesModalOpen}
            setEmbalajesModalOpen={setEmbalajesModalOpen}
            currentEmbalajeIndex={currentEmbalajeIndex}
            setCurrentEmbalajeIndex={setCurrentEmbalajeIndex}
          />
        </div>
      </div>
    </div>
  );
};

